import { z } from 'zod';

/**
 * data-model.md 2.1 的基礎型別。
 * TypeScript 型別一律由 Zod schema 推導，確保驗證與型別是同一個來源。
 */

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
const STICKER_PATTERN = /^(svg:[a-z0-9-]+|emoji:.+)$/u;

/** RFC 4122 v4 UUID，所有跨表關聯的唯一依據。 */
export const uuidSchema = z.string().regex(UUID_PATTERN, '必須是 UUID');

/** 本地日期，YYYY-MM-DD，無時區資訊。 */
export const localDateSchema = z
  .string()
  .regex(LOCAL_DATE_PATTERN, '必須是 YYYY-MM-DD')
  .refine(isRealCalendarDate, '不存在的日期');

/** 本地牆上時間，YYYY-MM-DDTHH:mm，**不帶時區偏移**。 */
export const localDateTimeSchema = z
  .string()
  .regex(LOCAL_DATE_TIME_PATTERN, '必須是 YYYY-MM-DDTHH:mm，且不帶時區偏移')
  .refine((value) => isRealCalendarDate(value.slice(0, 10)), '不存在的日期')
  .refine(isRealWallClockTime, '不存在的時間');

/** UTC 稽核時間，ISO 8601，結尾必須是 Z。 */
export const utcTimestampSchema = z
  .string()
  .regex(UTC_TIMESTAMP_PATTERN, '必須是結尾為 Z 的 UTC 時間');

/** 貼紙參照：內建圖案 svg:<key>，Emoji 為 emoji:<字元>。 */
export const stickerRefSchema = z.string().regex(STICKER_PATTERN, '必須是 svg: 或 emoji: 開頭');

export type Uuid = z.infer<typeof uuidSchema>;
export type LocalDate = z.infer<typeof localDateSchema>;
export type LocalDateTime = z.infer<typeof localDateTimeSchema>;
export type UtcTimestamp = z.infer<typeof utcTimestampSchema>;
export type StickerRef = z.infer<typeof stickerRefSchema>;

function isRealCalendarDate(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return false;
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day
  );
}

function isRealWallClockTime(value: string): boolean {
  const [hour, minute] = value.slice(11).split(':').map(Number);
  if (hour === undefined || minute === undefined) return false;
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
}

/**
 * 除 settings 與 attachments 外，所有實體都帶稽核欄位（data-model 2.3）。
 * revision 只在本機遞增，匯入時一律重設為 1。
 */
export const auditFieldsShape = {
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
  revision: z.number().int().positive(),
};

export const softDeleteShape = {
  deletedAt: utcTimestampSchema.nullable(),
};
